import { ErrorBoundaryScreen, type ErrorBoundaryAction } from '@verifime/components';
import { getSignedInUserId } from '@verifime/cognito-passwordless-auth';
import {
  buildNewRelicAttributes,
  computeErrorSignature,
  generateErrorReference,
  getOrCreateSessionId,
  recordOccurrence,
  reportErrorToNewRelic,
  routeWithoutQuery,
} from '@verifime/utils';
import { signOut } from 'amazon-cognito-passwordless-auth/common';
import { Component, ErrorInfo, ReactNode } from 'react';
import { ClarityLogo } from '@/components/ClarityLogo';
import { ConfirmationRefusedError } from '@/utils/ConfirmationRefusedError';
import * as messages from '@/utils/messages';
import { getTrackIdFromStorage, getTrackIdFromUrl } from '@/utils/utils';

type Props = { children: ReactNode };
type State = {
  hasError: boolean;
  reference: string;
  isRecurring: boolean;
  notified: boolean;
};

/**
 * Best-effort, never-throwing trackId for the post-logout sign-in redirect.
 * Tries the URL path, then the query string, then sessionStorage (TRACK_ID),
 * the same sources the sign-up flow uses. Usually null for an established
 * dashboard user (the key is cleared once onboarding completes), in which case
 * the caller falls back to the generic landing page.
 */
function resolveTrackId(): string | null {
  try {
    // /sign-up/track-id/<id> and /recover-invitation/<id> are Next dynamic
    // routes, so their trackId is a path segment (router.query.trackId), not a
    // ?trackId= query param. getTrackIdFromUrl() reads the query string only and
    // would miss it, so parse the path here.
    const pathMatch = window.location.pathname.match(
      /^\/(?:sign-up\/track-id|recover-invitation)\/([^/]+)/,
    );
    if (pathMatch?.[1]) return decodeURIComponent(pathMatch[1]);
    // Query string, then sessionStorage, via the shared helpers.
    return getTrackIdFromUrl() ?? getTrackIdFromStorage();
  } catch {
    return null;
  }
}

/**
 * The New Relic attribute that names the cause of a refused confirmation
 * (Greengate-Fintech/verifime-adr#690). Only a `ConfirmationRefusedError` gets one: any other error
 * reports as before, even if it carries an `errorCode` property of its own.
 */
function refusalAttributesOf(error: Error): { errorCode?: string } {
  return error instanceof ConfirmationRefusedError ? { errorCode: error.errorCode } : {};
}

/**
 * Intentionally a class component, even as the codebase moves to function
 * components. React error boundaries can only be classes: there is no
 * functional or hook equivalent for the error-catching lifecycle
 * (`static getDerivedStateFromError` and `componentDidCatch`). The only
 * alternative is the `react-error-boundary` package, which still wraps a class
 * internally. See https://react.dev/reference/react/Component (Catching
 * rendering errors with an Error Boundary).
 */
export default class PortalErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, reference: '', isRecurring: false, notified: false };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const componentStack = info.componentStack ?? '';
    const signature = computeErrorSignature(error, componentStack);
    const storage = typeof window !== 'undefined' ? window.sessionStorage : null;
    const { count, isRecurring } = recordOccurrence(signature, Date.now(), storage);
    const reference = generateErrorReference();
    const sessionId = getOrCreateSessionId(storage);
    const route = typeof window !== 'undefined' ? routeWithoutQuery(window.location.pathname) : '';

    let userId: string | undefined;
    try {
      userId = getSignedInUserId() ?? undefined;
    } catch {
      // best-effort identity locator; omit when unavailable (signed out / storage blocked)
    }

    const notified = reportErrorToNewRelic(error, {
      ...buildNewRelicAttributes(componentStack, {
        product: 'wallet',
        errorReference: reference,
        errorSignature: signature,
        occurrenceCount: count,
        route,
        appVersion: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? 'local',
        environment: process.env.NEXT_PUBLIC_VERCEL_ENV ?? 'local',
        sessionId,
        userId,
      }),
      ...refusalAttributesOf(error),
    });

    if (isRecurring && typeof window !== 'undefined') {
      // Force logout on a recurring crash: clears likely-corrupt session state.
      // Uses the configured passwordless library (proxy-backed per ADR 92).
      try {
        signOut({}).signedOut.catch(() => {});
      } catch {
        // signOut can throw synchronously (e.g. the library is not configured
        // because the crash was above PasswordlessContextProvider). Never let it
        // escape componentDidCatch, or the fallback unmounts and we white-screen.
      }
    }

    this.setState({ reference, isRecurring, notified });
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    const { reference, isRecurring, notified } = this.state;
    const supportHref = `mailto:${messages.SUPPORT_EMAIL}?subject=Wallet%20error%20${reference}`;

    const primaryAction: ErrorBoundaryAction = isRecurring
      ? {
          label: messages.ERROR_ACTION_CONTACT_SUPPORT,
          onClick: () => window.location.assign(supportHref),
          variant: 'contained',
          'data-action': 'contact-support',
        }
      : {
          label: messages.ERROR_ACTION_TRY_AGAIN,
          onClick: () => window.location.reload(),
          variant: 'contained',
          'data-action': 'retry',
        };

    const secondaryAction: ErrorBoundaryAction | undefined = isRecurring
      ? {
          label: messages.ERROR_ACTION_BACK_TO_SIGN_IN,
          // After a force-logout, send the user to the track-id sign-in screen
          // (magic-link + Google SSO) when we can recover a trackId; otherwise
          // fall back to the generic landing/sign-in page.
          onClick: () => {
            const trackId = resolveTrackId();
            window.location.assign(
              trackId ? `/sign-up/track-id/${encodeURIComponent(trackId)}` : '/',
            );
          },
          variant: 'outlined',
          'data-action': 'sign-in',
        }
      : undefined;

    return (
      <ErrorBoundaryScreen
        data-testid="portal-error-boundary"
        product="wallet"
        logo={<ClarityLogo size="small" />}
        headline={messages.ERROR_HEADLINE}
        body={isRecurring ? messages.ERROR_BODY_RECURRING : messages.ERROR_BODY_TRANSIENT}
        securityNote={messages.ERROR_SECURITY_NOTE}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        loopHint={isRecurring ? messages.ERROR_LOOP_HINT : undefined}
        referenceCode={reference}
        showNotified={notified}
        notifiedText={messages.ERROR_NOTIFIED}
      />
    );
  }
}
